import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { Dimmer, Loader, Dropdown} from 'semantic-ui-react';
import { AddButton } from '../../../components/AddButton/AddButton';
import {useReport} from '../../../providers/ReportProvider';
import {useCustomers} from '../../../providers/CustomerProvider';
import {useAuth} from "../../../providers/AuthenticationProvider";
const WEB_PAGE = 'Reports';

const dateFormatter = date => {
  const d = new Date(date + 'Z');
  return d.toLocaleString();
};

export const ReportList = () => {
  let queryParams = new URLSearchParams(window.location.search);
  let initialCustomerId = queryParams.has('customerId')
    ? parseInt(queryParams.get('customerId'))
    : null;

  const { checkAuth, getUserType } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pollingActive, setPollingActive] = useState(false);
  const [pollingIndex, setPollingIndex] = useState(0);
  const {customersList, getAllCustomers} = useCustomers();
  const {
    total,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    reports,
    listReports,
    sortState,
    setSortState,
  } = useReport();
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState(initialCustomerId);

  const handlePageChange = (e) => {
    setPageIndex(e.page.skip / e.page.take);
  };

  const handleSortChange = (e) => {
    setSortState(e.sort.length ? [{ fieldName: e.sort[0].field, dir: e.sort[0].dir }] : []);
  };

  const sort = sortState.map(s => ({ field: s.fieldName, dir: s.dir }));

  checkAuth(getUserType(), WEB_PAGE) && navigate('/');

  useEffect(() => {
    getAllCustomers();
  }, [listReports, pageSize, pageIndex, getAllCustomers]);

  useEffect(() => {
    if (customersList.length === 1) {
      setCustomerId(customersList[0].id);
    }
  }, [customersList, setCustomerId]);

  useEffect(() => {
    if (customerId != null) {
      setLoading(true);
      const fetchReports = async () => {
        await listReports(customerId);
        setLoading(false);
      };
      fetchReports();
    }
  }, [customerId, listReports, pollingIndex]);

  useEffect(() => {
    let pollingRequired = false;
    reports.forEach(r => {
      if (r.status === 0) {
        pollingRequired = true;
      }
    });
    setPollingActive(pollingRequired);
  }, [reports, setPollingActive]);

  useEffect(() => {
    if (pollingActive) {
      setTimeout(() => {
        // increment polling index to re-trigger existing useEffect to get data
        setPollingIndex(pollingIndex + 1);
      }, 5000);
    }
  }, [pollingActive, pollingIndex, setPollingIndex]);

  return (
    <div>
      <div className="pageHeader">
        <h1>Reports</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Dropdown
            placeholder="Select Customer"
            selection
            search
            options={customersList.map(x => {
              const text =
                x?.firstname && x?.lastname
                  ? `${x?.companyName} (${x?.firstname} ${x?.lastname})`
                  : x?.companyName;
              return {
                key: x?.id,
                text: text,
                value: x?.id,
              };
            })}
            value={customerId}
            style={{ height: '38px', minWidth: '280px' }}
            onChange={(e, selected) => {
              const customer = customersList.find(x => x.id === selected.value);
              setCustomerId(customer.id);
            }}
          />
          <AddButton onClick={() => navigate('/reports/create')}>Create Report</AddButton>
        </div>
      </div>
      {!customerId && (
        <div style={{ textAlign: 'center', marginTop: '3rem', color: '#999', fontSize: '1.1rem' }}>
          Select a customer to view reports
        </div>
      )}
      {customerId && (
        <div style={{ position: 'relative' }}>
          <Dimmer active={loading} inverted>
            <Loader />
          </Dimmer>
          <Grid
            data={reports}
            total={total}
            skip={pageIndex * pageSize}
            take={pageSize}
            pageable={true}
            sortable={true}
            sort={sort}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            onRowClick={(e) => window.open(e.dataItem.url)}
          >
            <Column field="id" title="ID" />
            <Column field="name" title="Report Name" width={550} cell={props => <td>{props.dataItem.name ?? ''}</td>} />
            <Column field="statusName" title="Status" />
            <Column
              field="createdUtc"
              title="Date"
              cell={props => <td>{dateFormatter(props.dataItem.createdUtc)}</td>}
            />
          </Grid>
        </div>
      )}
    </div>
  );
};
